import { Fragment, h } from "preact";

import HeaderIcon from "./HeaderIcon.jsx";

import i18n from "../i18n";
import { incomeToK } from "../salaryHelper.js";
import SalaryComparison from "./loggedOutSalaryCalculator/SalaryComparison.jsx";
import MonthlyExpenses from "./loggedOutSalaryCalculator/MonthlyExpenses.jsx";
import MonthlySalary from "./loggedOutSalaryCalculator/MonthlySalary.jsx";
import LoadWidget from "./LoadWidget.jsx";

/**
 * This is shown when the user sees the result the first time.
 */
export default function SalaryCalculatorResult({ data }) {
  return (
    <Fragment>
      <HeaderIcon>salary_calculator</HeaderIcon>
      <div class="task-page__content">
        {data.calculated ? (
          <p class="t-body3 mb-32" data-qa="result-headline">
            To maintain your standard of living in Germany, you&apos;ll need:
          </p>
        ) : (
          <p class="t-body3 mb-32">
            Unfortunately, we cannot offer an individual calculation for your
            country. However, we can share the average IT salary in Germany,
            which serves as a reference point:
          </p>
        )}

        <div class="salary-calculator-result__display-items">
          <div class="salary-calculator-result__display-item">
            <span class="salary-calculator-result__display-item-value">
              <span
                class="salary-calculator-result__display-item-value-calc"
                data-qa="gross-income-result"
              >
                {incomeToK(data.grossIncome)}
              </span>
            </span>
            <span class="salary-calculator-result__display-item-title">
              {i18n.t("pages.salary_calculator.result.gross_income")}
            </span>
          </div>
          <div class="salary-calculator-result__display-item">
            <span class="salary-calculator-result__display-item-value">
              <span
                class="salary-calculator-result__display-item-value-calc"
                data-qa="net-income-result"
              >
                {incomeToK(data.netIncome)}
              </span>
            </span>
            <span class="salary-calculator-result__display-item-title">
              {i18n.t("pages.salary_calculator.result.net_income")}
            </span>
          </div>
        </div>

        <SalaryComparison
          inApp
          class="mt-48"
          result={data}
          type="salary"
          showEstimate={data.calculated}
        />
        {data.calculated && (
          <MonthlySalary
            inApp
            highlighted
            class="mt-96"
            result={data}
            type="salary"
          />
        )}
        <MonthlyExpenses inApp class="mt-96" result={data} type="salary" />

        <div class="mt-96">
          <h3 class="t-h6 color-brand-midnight">Cost of living in Germany</h3>
          <p class="t-body3 color-shades-charcoal">
            Despite being one of the wealthiest countries globally, the cost of
            living in Germany is relatively affordable compared to other
            European countries. As a basic estimate, an individual can expect to
            spend between €1,500 and €3,000 per month. However, expenses can
            vary based on location, household size, and lifestyle.
          </p>
          <p class="t-body3 color-shades-charcoal">
            Living in major cities like Munich and Stuttgart tends to be more
            expensive due to higher rent prices. Once considered affordable,
            Berlin has witnessed a rapid increase in rent prices in recent
            years. However, cities like Leipzig and Dresden still offer
            affordable living options.
          </p>
          <p class="t-body3 color-shades-charcoal">
            As of April 2023, Germany experienced an inflation rate of 7.2%,
            with the most significant rises in energy and food prices. Inflation
            is projected to remain high until the end of 2024. As we move
            through 2023, we will frequently update the numbers to obtain an
            accurate understanding of the current situation.
          </p>
        </div>

        <LoadWidget hideDefault taskId="job_search" class="mt-64" />

        <div class="mt-64">
          <h3 class="t-h6 color-brand-midnight">How we calculate</h3>

          <p class="t-body3 color-shades-charcoal">
            To estimate your salary, we compare your current cost of living with
            the cost of living in Germany based on purchasing power parity.
            Purchasing power parity helps us determine the differences in the
            cost of living between two countries. This calculation gives us your
            net salary.
          </p>
          <p class="t-body3 color-shades-charcoal">
            At Nioomi, we rely on the latest data from the{" "}
            <a
              href="https://www.bundesfinanzministerium.de/Content/DE/Standardartikel/Service/Abgabenrechner/interaktiver-abgabenrechner.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Federal Ministry of Finance
            </a>{" "}
            to calculate your gross salary and the taxes and social security
            contributions deducted from your net pay. It&apos;s important to
            note that the final calculation may have slight variations, as we
            don&apos;t consider factors like the specific federal state or
            religious affiliation.
          </p>
          <p class="t-body3 color-shades-charcoal">
            The{" "}
            <a
              href="https://www.destatis.de/EN/Themes/Society-Environment/Income-Consumption-Living-Conditions/Consumption-Expenditure/_node.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Federal Statistical Office
            </a>{" "}
            gathers data on the monthly net household income and the related
            living expenses of private households on an annual basis. We utilize
            this information to provide you with an estimate of the expected
            cost of living in Germany.
          </p>
        </div>
      </div>
    </Fragment>
  );
}
